import _ from 'lodash'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useAudioInputs, useVideoInputs } from 'amazon-chime-sdk-component-library-react'
import { useSnackbar } from 'notistack'

import { useRequestHandler } from '@/hooks/useRequestHandler'

import { withChime } from '@/providers/ChimeProvider'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { leaveMeeting, selectAttendeeInfo, selectConsultationById, selectMeetingInfo } from '@/redux/reducers/consultation.reducer'
import { selectCustomDevices } from '@/redux/reducers/custom-device.reducer'
import { addDocument, selectDocumentCreateRequestTracker } from '@/redux/reducers/document.reducer'

import { getVisioConferenceCameras, getVisioConferenceMicros } from '@/utils/devices'

import ChimeMeeting, { ChimeMeetingProps, SCREENSHOT_PREFIX } from '../components/ChimeMeeting'

type ChimeMeetingContainerProps = Pick<ChimeMeetingProps, 'disableScreenshot'> & {
  joinMeeting: () => void
  inputConsultationId?: string
  disableOpenInNewTab?: boolean
  onReconnectToConsultation?: () => void
}

export function ChimeMeetingContainer({ disableScreenshot, joinMeeting, inputConsultationId, disableOpenInNewTab }: ChimeMeetingContainerProps) {
  const dispatch = useAppDispatch()
  const { devices: chimeAudioInputDevices } = useAudioInputs()
  const { devices: chimeVideoInputDevices } = useVideoInputs()
  const { consultationId } = useParams()

  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const createDocumentRequestHandler = useRequestHandler({
    onSuccess: () => enqueueSnackbar(t('notification.document.create.success'), { variant: 'success' }),
    onError: () => enqueueSnackbar(t('notification.document.create.error'), { variant: 'error' }),
    requestTrackerSelector: selectDocumentCreateRequestTracker,
  })

  const consultation = useAppSelector((state) =>
    consultationId || inputConsultationId ? selectConsultationById(state, consultationId ?? inputConsultationId ?? '') : undefined
  )
  const customDevices = useAppSelector(selectCustomDevices)
  const attendeeInfo = useAppSelector((state) => selectAttendeeInfo(state, 'visioConference'))
  const meetingInfo = useAppSelector((state) => selectMeetingInfo(state, 'visioConference'))

  const audioInputMediaDevices = useMemo(
    () => chimeAudioInputDevices.map((device) => _.merge(device, { kind: 'audioinput' }) as MediaDeviceInfo),
    [chimeAudioInputDevices]
  )
  const videoMediaDevices = useMemo(
    () => _.map(chimeVideoInputDevices, (device) => _.merge(device, { kind: 'videoinput' }) as MediaDeviceInfo),
    [chimeVideoInputDevices]
  )

  const visioConferenceDevices = useMemo(() => {
    const visioConferenceCameras = _.map(getVisioConferenceCameras(customDevices, videoMediaDevices), (visioConferenceCameraDevice) => ({
      id: visioConferenceCameraDevice.device.deviceId,
      isCustom: true,
      isSelected: false,
      label: visioConferenceCameraDevice.customName,
      iconURL: visioConferenceCameraDevice.iconURL,
      type: visioConferenceCameraDevice.device.type,
    }))

    const visioConferenceMicros = _.map(getVisioConferenceMicros(customDevices, audioInputMediaDevices), (visioConferenceCameraDevice) => ({
      id: visioConferenceCameraDevice.device.deviceId,
      isCustom: true,
      isSelected: false,
      label: visioConferenceCameraDevice.customName,
      iconURL: visioConferenceCameraDevice.iconURL,
      type: visioConferenceCameraDevice.device.type,
    }))

    return [...visioConferenceCameras, ...visioConferenceMicros]
  }, [audioInputMediaDevices, videoMediaDevices, customDevices])

  const screenshotCount = useMemo(() => {
    if (!consultation) return 0

    return _.filter(
      consultation.files,
      (file) => file.filename.startsWith(SCREENSHOT_PREFIX) && _.some(['png', 'jpg', 'jpeg'], (ext) => file.filename.endsWith(ext))
    ).length
  }, [consultation])

  const handleScreenshot = (imageFile: File) => {
    console.log('SCREENSHOT CHIME')
    if (consultationId) createDocumentRequestHandler.dispatch(addDocument({ documentFile: imageFile, objectType: 'consultation', objectId: consultationId }))
  }

  useEffect(() => {
    return () => {
      dispatch(leaveMeeting('visioConference'))
    }
  }, [dispatch])

  useEffect(() => {
    joinMeeting()
  }, [joinMeeting])

  return (
    <ChimeMeeting
      disableScreenshot={disableScreenshot}
      localAttendeeInfo={attendeeInfo}
      meetingInfo={meetingInfo}
      onReconnection={joinMeeting}
      customDevices={visioConferenceDevices}
      onScreenshot={handleScreenshot}
      screenshotCount={screenshotCount}
      disableOpenInNewTab={disableOpenInNewTab}
      consultationId={consultationId}
    />
  )
}

export default withChime(ChimeMeetingContainer)
